import React, { useState, useEffect } from 'react';
import { useContent } from '../contexts/ContentContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { resolvePhotoAlbums, resolveVideoAlbums, countGalleryVideos } from '../utils/galleryAlbums';
import GalleryVideoItem from './GalleryVideoItem';

const renderGalleryPhoto = (photo, index, keyPrefix = '') => {
  const photoUrl = getMediaUrl(photo);
  return (
    <div
      key={`${keyPrefix}${index}`}
      className="gallery-item"
      style={photo.imageSettings?.aspectRatio ? { aspectRatio: 'auto' } : {}}
    >
      {photoUrl ? (
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingBottom: photo.imageSettings?.aspectRatio
              ? `${(1 / (() => {
                  const ratios = {
                    '1:1': 1,
                    '4:3': 4 / 3,
                    '16:9': 16 / 9,
                    '4:5': 4 / 5,
                    '3:2': 3 / 2,
                    '21:9': 21 / 9,
                  };
                  return ratios[photo.imageSettings.aspectRatio] || 16 / 9;
                })()) * 100}%`
              : '56.25%',
            overflow: 'hidden',
          }}
        >
          <img
            src={photoUrl}
            alt={photo.title}
            style={
              photo.imageSettings
                ? {
                    transform: `translate(calc(-50% + ${photo.imageSettings.position.x}px), calc(-50% + ${photo.imageSettings.position.y}px)) scale(${photo.imageSettings.scale})`,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    minWidth: '100%',
                    minHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: photo.imageSettings.objectFit || 'cover',
                  }
                : {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }
            }
          />
        </div>
      ) : (
        <div className="gallery-placeholder" title={photo.title}>
          <i className={photo.icon || 'fas fa-camera'}></i>
        </div>
      )}
      <div className="gallery-overlay">
        <h4>{photo.title}</h4>
      </div>
    </div>
  );
};

const Galerie = () => {
  const { getContent } = useContent();
  const expertGallery = getContent('home.expertGallery') || {};
  const defaultTab = expertGallery.defaultTab || 'photos';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const photoAlbums = resolvePhotoAlbums(expertGallery);
  const videoAlbums = resolveVideoAlbums(expertGallery);

  useEffect(() => {
    if (expertGallery.defaultTab) {
      setActiveTab(expertGallery.defaultTab);
    }
  }, [expertGallery.defaultTab]);

  const totalPhotos = photoAlbums.reduce((sum, album) => sum + (album.photos?.length || 0), 0);
  const totalVideos = countGalleryVideos(expertGallery);
  const tabs = expertGallery.tabs || [
    { id: 'photos', icon: 'fas fa-images', text: 'Photos' },
    { id: 'videos', icon: 'fas fa-video', text: 'Vidéos' },
  ];

  return (
    <div className="page-wrapper">
      <section className="gallery-full-page" style={{ padding: '60px 0', background: '#f8f9fa' }}>
        <div className="container">
          <div
            className="gallery-tabs"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '40px',
              background: '#fff',
              borderRadius: '12px',
              padding: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 30px',
                  background: activeTab === tab.id ? '#0ea5e9' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.id ? '0 2px 4px rgba(14, 165, 233, 0.3)' : 'none',
                }}
              >
                <i className={tab.icon} style={{ marginRight: '8px' }}></i>
                {tab.text}
              </button>
            ))}
          </div>

          <div className="gallery-wrapper">
            {activeTab === 'photos' && (
              <div className="gallery-albums-page">
                {totalPhotos === 0 ? (
                  <div
                    className="gallery-empty-state"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4rem 2rem',
                      textAlign: 'center',
                      background: '#fff',
                      borderRadius: '16px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      minHeight: '400px',
                    }}
                  >
                    <div
                      style={{
                        width: '120px',
                        height: '120px',
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.5rem',
                        boxShadow: '0 8px 24px rgba(14, 165, 233, 0.3)',
                      }}
                    >
                      <i className="fas fa-images" style={{ fontSize: '3rem', color: '#fff' }}></i>
                    </div>
                    <h3
                      style={{
                        margin: '0 0 0.75rem 0',
                        color: '#1e293b',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                      }}
                    >
                      Aucune photo pour le moment
                    </h3>
                    <p
                      style={{
                        margin: '0 0 2rem 0',
                        color: '#64748b',
                        fontSize: '1rem',
                        maxWidth: '400px',
                      }}
                    >
                      Les photos de nos formations et conférences seront ajoutées prochainement. Revenez
                      bientôt!
                    </p>
                  </div>
                ) : (
                  photoAlbums.map((album) => (
                    <div key={album.id || album.title} className="gallery-album-section">
                      {photoAlbums.length > 1 && (
                        <h2 className="gallery-album-title">{album.title}</h2>
                      )}
                      <div className="gallery-content photos-content">
                        {(album.photos || []).map((photo, index) =>
                          renderGalleryPhoto(photo, index, `${album.id}-`)
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'videos' && (
              <div className="gallery-albums-page">
                {totalVideos === 0 ? (
                  <div
                    className="gallery-empty-state"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4rem 2rem',
                      textAlign: 'center',
                      background: '#fff',
                      borderRadius: '16px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      minHeight: '400px',
                    }}
                  >
                    <div
                      style={{
                        width: '120px',
                        height: '120px',
                        background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.5rem',
                        boxShadow: '0 8px 24px rgba(255, 0, 0, 0.3)',
                      }}
                    >
                      <i className="fab fa-youtube" style={{ fontSize: '3rem', color: '#fff' }}></i>
                    </div>
                    <h3
                      style={{
                        margin: '0 0 0.75rem 0',
                        color: '#1e293b',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                      }}
                    >
                      Aucune vidéo pour le moment
                    </h3>
                    <p
                      style={{
                        margin: '0 0 2rem 0',
                        color: '#64748b',
                        fontSize: '1rem',
                        maxWidth: '400px',
                      }}
                    >
                      Nos vidéos YouTube de formations et témoignages seront ajoutées prochainement.
                      Restez connectés!
                    </p>
                  </div>
                ) : (
                  videoAlbums.map((album) => (
                    <div key={album.id || album.title} className="gallery-album-section">
                      {videoAlbums.length > 1 && album.title && (
                        <h2 className="gallery-album-title">{album.title}</h2>
                      )}
                      <div className="gallery-content videos-content">
                        {(album.videos || []).map((video, index) => (
                          <GalleryVideoItem
                            key={`${album.id}-${index}`}
                            video={video}
                            index={index}
                            keyPrefix={`${album.id}-`}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Galerie;
